import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { siteIdentity } from "@/data/siteIdentity";

declare const __BUILD_TIME__: string | undefined;

// Sajt-unika kategorigrupper. Avsiktligt asymmetrisk struktur (2 kolumner med
// olika antal länkar) för att bryta den symmetriska 4-col-mall som de övriga
// nätverkssajterna använder.
const PRIMARY_CATEGORIES = [
  { slug: "money-finance", label: "Money & Finance" },
  { slug: "housing-renting", label: "Housing & Renting" },
  { slug: "home-repairs", label: "Home & Repairs" },
  { slug: "life-admin", label: "Life Admin" },
  { slug: "work-career", label: "Work & Career" },
];

const SECONDARY_CATEGORIES = [
  { slug: "food-cooking", label: "Food & Cooking" },
  { slug: "cleaning-routines", label: "Cleaning & Routines" },
  { slug: "social-relationships", label: "Social & Relationships" },
  { slug: "society-systems", label: "Society & Systems" },
  { slug: "transport-driving", label: "Transport & Driving" },
  { slug: "insurance-protection", label: "Insurance & Protection" },
  { slug: "digital-life", label: "Digital Life" },
];

const Footer = () => {
  return (
    <footer className="border-t border-border mt-auto bg-muted/20">
      <div className="container py-14">
        {/* Top: editor card + categories */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
          {/* Editor card — distinct, sajt-unique element */}
          <div className="lg:col-span-5">
            <Link to="/" className="font-heading text-2xl font-bold text-foreground inline-block mb-3">
              Life Basics Guide
            </Link>
            <p className="font-heading italic text-base text-muted-foreground max-w-md leading-relaxed mb-5">
              Clear answers for the things nobody taught you.
            </p>
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed mb-5">
              An independent reference site for adults catching up on the
              practical bits — paperwork, money, housing, and the small civic
              systems that hold daily life together.
            </p>
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <Link
                to="/author/carl-molner"
                className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-card border border-border flex items-center justify-center text-[10px] font-heading font-semibold">
                  CM
                </span>
                Edited by Carl S. Molner
              </Link>
              <span aria-hidden className="text-border">·</span>
              <a
                href="mailto:hello@lifebasicsguide.com"
                className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
              >
                <Mail className="h-3.5 w-3.5" />
                hello@lifebasicsguide.com
              </a>
            </div>
          </div>

          {/* Categories — two asymmetric columns instead of one regular grid */}
          <div className="lg:col-span-4">
            <h3 className="label-editorial mb-4">Everyday topics</h3>
            <ul className="space-y-2.5">
              {PRIMARY_CATEGORIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/category/${c.slug}`}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="label-editorial mb-4">More</h3>
            <ul className="space-y-2.5">
              {SECONDARY_CATEGORIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/category/${c.slug}`}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Quick site nav — horizontal strip */}
        <div className="flex flex-wrap gap-x-6 gap-y-2 pb-6 border-b border-border text-sm">
          <Link to="/categories" className="text-muted-foreground hover:text-foreground transition-colors">
            All categories
          </Link>
          <Link to="/glossary" className="text-muted-foreground hover:text-foreground transition-colors">
            Glossary
          </Link>
          <Link to="/tools" className="text-muted-foreground hover:text-foreground transition-colors">
            Tools
          </Link>
          <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">
            About
          </Link>
          <Link to="/editorial-policy" className="text-muted-foreground hover:text-foreground transition-colors">
            Editorial policy
          </Link>
          <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">
            Contact
          </Link>
        </div>

        {/* Bottom strip — minimal, single row, copyright + legal */}
        <div className="pt-6 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {siteIdentity.brand}. {siteIdentity.footerSuffix}
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
            <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
              Terms
            </Link>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("show-cookie-preferences"))}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Cookie preferences
            </button>
          </div>
        </div>
        <p className="mt-5 text-[10px] text-muted-foreground">
          Build {typeof __BUILD_TIME__ !== "undefined" ? __BUILD_TIME__ : "dev"}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
